import React, { forwardRef, SelectHTMLAttributes, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { SearchableSelect, SelectOption } from './SearchableSelect';

type Props = SelectHTMLAttributes<HTMLSelectElement> & {
  /** Search box in the menu. Default: on for lists longer than 8. */
  searchable?: boolean;
  placeholder?: string;
};

/**
 * Drop-in for a native <select>: same props, same <option>/<optgroup> children,
 * same `onChange(e)` — but drawn as the app's SearchableSelect, because the OS
 * menu (a dark macOS popup over the field) looked foreign everywhere.
 *
 * A hidden real <select> stays in the DOM and is the source of truth: refs,
 * `name`, `required` and react-hook-form keep working, and choosing an option
 * writes its value there and fires a genuine `change` event, so every existing
 * `e.target.value` handler runs unchanged.
 */
export const Select = forwardRef<HTMLSelectElement, Props>(({
  className, children, value, defaultValue, onChange, disabled, multiple, id, style, searchable, placeholder, title, ...rest
}, ref) => {
  const nativeRef = useRef<HTMLSelectElement>(null);
  useImperativeHandle(ref, () => nativeRef.current as HTMLSelectElement);
  const parsed = useMemo(() => optionsFrom(children), [children]);
  const controlled = value !== undefined;
  const [inner, setInner] = useState(() => String(defaultValue ?? parsed.options.find((o) => !o.disabled)?.value ?? ''));
  // Uncontrolled selects can be changed from outside (form.reset, setValue) — read the real element back
  // after every render; it only sets state when the value differs, so it can't loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (!controlled && nativeRef.current && nativeRef.current.value !== inner) setInner(nativeRef.current.value);
  });

  if (multiple) {
    return <select ref={nativeRef} multiple id={id} value={value} defaultValue={defaultValue} onChange={onChange} disabled={disabled} style={style} title={title} className={clsx('w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary', className)} {...rest}>{children}</select>;
  }

  const current = controlled ? String(value ?? '') : inner;
  const choose = (next: string) => {
    const el = nativeRef.current;
    if (!el) return;
    Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value')?.set?.call(el, next);
    el.dispatchEvent(new Event('change', { bubbles: true }));
    if (!controlled) setInner(next);
  };
  const compact = /(^|\s)(text-xs|text-\[1[0-2]px\]|h-[6-9]|py-0\.5|py-1)(\s|$)/.test(className ?? '');
  const hiddenPlaceholder = parsed.hidden.find((o) => o.value === current)?.label;

  return (
    <div className={clsx('relative min-w-0', layoutClasses(className))} style={style}>
      {/* The trigger comes first: a wrapping <label> activates the first form control inside it. */}
      <SearchableSelect
        id={id}
        options={parsed.options}
        value={current}
        onChange={choose}
        disabled={disabled}
        numbered={false}
        searchable={searchable ?? parsed.options.length > 8}
        placeholder={hiddenPlaceholder ?? placeholder ?? 'Select…'}
        aria-label={rest['aria-label'] ?? title}
        size={compact ? 'sm' : 'md'}
      />
      <select
        ref={nativeRef}
        aria-hidden
        tabIndex={-1}
        className="sr-only"
        {...(controlled ? { value } : { defaultValue })}
        onChange={onChange ?? (() => undefined)}
        disabled={disabled}
        {...rest}
      >
        {children}
      </select>
    </div>
  );
});
Select.displayName = 'Select';

/** Width / spacing / grid placement from the old select's classes; its own border, padding and colours belong to the trigger now. */
function layoutClasses(className?: string): string {
  return (className ?? '').split(/\s+/).filter((c) => {
    const base = c.replace(/^([a-z0-9-]+:)+/, '').replace(/^!/, '');
    return /^(w-|min-w-|max-w-|flex-|grow|shrink|basis-|col-|row-|order-|self-|justify-self-|-?m[trblxy]?-|hidden$|block$|inline-block$|sr-only$)/.test(base);
  }).join(' ');
}

function text(node: React.ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(text).join('');
  if (React.isValidElement(node)) return text((node.props as { children?: React.ReactNode }).children);
  return '';
}

function optionsFrom(children: React.ReactNode): { options: SelectOption[]; hidden: SelectOption[] } {
  const options: SelectOption[] = [];
  const hidden: SelectOption[] = [];
  const walk = (nodes: React.ReactNode, group?: string, groupDisabled?: boolean) => {
    React.Children.forEach(nodes, (child) => {
      if (!React.isValidElement(child)) return;
      const props = child.props as { value?: unknown; label?: string; disabled?: boolean; hidden?: boolean; children?: React.ReactNode };
      if (child.type === React.Fragment) { walk(props.children, group, groupDisabled); return; }
      if (child.type === 'optgroup') { walk(props.children, props.label, props.disabled); return; }
      if (child.type !== 'option') return;
      const label = (props.label ?? text(props.children)).trim();
      const opt: SelectOption = { value: String(props.value ?? label), label: label || '—', ...(group ? { group } : {}), ...(props.disabled || groupDisabled ? { disabled: true } : {}) };
      (props.hidden ? hidden : options).push(opt);
    });
  };
  walk(children);
  return { options, hidden };
}
